<script setup>
import { ref, onMounted, computed, watch, nextTick } from 'vue'
import { mediaUrl } from '@core/utils/mediaUrl'
import moment from 'moment-timezone'
import VueApexCharts from 'vue3-apexcharts'
import { useInstantBookingSlotsStore, useRestaurantsStore, useUsersStore, useUserGroupsStore } from '@/stores'
import { storeToRefs } from 'pinia'
import { reportError, numberWithCommas } from '@/plugins/misc'
import { requiredValidator } from '@validators'
import AppDateTimePicker from '@core/components/AppDateTimePicker.vue'
import GooglePlacesService from '@/services/GooglePlacesService'
import { VuetifyTiptap } from 'vuetify-pro-tiptap'
import CommsHistoryPanel from '@core/components/CommsHistoryPanel.vue'

// Stores
const instantBookingSlotsStore = useInstantBookingSlotsStore()
const restaurantsStore = useRestaurantsStore()
const usersStore = useUsersStore()
const userGroupsStore = useUserGroupsStore()

const { myUser } = storeToRefs(usersStore)
const { adminSlots: slots, loading, analytics } = storeToRefs(instantBookingSlotsStore)
const { restaurantUserGroups } = storeToRefs(userGroupsStore)

// TipTap editor configuration
const tiptapOutput = ref('html')
const tiptapOutlined = ref(true)
const tiptapMaxWidth = ref(900)

// Check if user is platform admin
const isPlatformAdmin = computed(() => myUser.value?.userLevel === 'admin')

// Check if user is restaurant admin (has restaurantUserGroups)
const isRestaurantAdmin = computed(() => {
  return restaurantUserGroups.value && restaurantUserGroups.value.length > 0
})

// Get list of restaurant IDs the user has admin access to
const myRestaurantIds = computed(() => {
  if (!restaurantUserGroups.value) return []

  return restaurantUserGroups.value.map(rug => rug.restaurant?._id).filter(Boolean)
})

// Get list of restaurants from user's restaurant user groups (for autocomplete)
const myRestaurants = computed(() => {
  if (!restaurantUserGroups.value) return []

  return restaurantUserGroups.value
    .filter(rug => rug.restaurant)
    .map(rug => ({
      _id: rug.restaurant._id,
      name: rug.name || rug.restaurant.name,
      market: rug.restaurant.market,
      imageIds: rug.restaurant.imageIds,
      googlePlaceId: rug.restaurant.googlePlaceId,
      googlePlaceDetails: rug.restaurant.googlePlaceDetails,
    }))
})

// Check if user has access (platform admin or restaurant admin)
const hasAccess = computed(() => isPlatformAdmin.value || isRestaurantAdmin.value)

// Track whether we've finished checking access
const accessCheckComplete = ref(false)

// Check if user is admin and fetch user groups if needed
onMounted(async () => {
  // Ensure store cache is loaded first
  await userGroupsStore.loadInitialData()

  // Wait for myUser to be available
  await nextTick()

  if (!myUser.value) {
    window.location.href = '/'

    return
  }

  // Platform admin: immediate access
  if (myUser.value.userLevel === 'admin') {
    accessCheckComplete.value = true

    return
  }

  // Not platform admin: fetch user groups to check restaurant admin status
  await userGroupsStore.getMyUserGroups()

  // Wait for Vue reactivity to propagate
  await nextTick()

  accessCheckComplete.value = true

  // Check access after loading user groups
  if (!hasAccess.value) {
    window.location.href = '/'
  }
})

// Tab state
const currentTab = ref('create')

// ============================================================================
// CREATE SLOTS TAB
// ============================================================================

const createForm = ref({
  restaurantId: null,
  slotType: null, // null = internal (default), 'external' = external
  externalBookingUrl: '',
  showGeneralAvailability: true,
  experienceName: 'Platinum Access',
  experienceDescription: 'This reservation is secured by a $xxxpp Food & Beverage minimum',
  experienceImageId: null,
  slots: [{
    date: '2026-02-14',
    time: '19:30',
    partySize: 2,
    seatingArea: '',
    guaranteedSpendPerPerson: 200,
    taxPercentage: 12,
    tipPercentage: 20,
    ccFeePercentage: 0,
    paymentCaptureType: 'hold',
    cancellationPeriodHours: 24,
    penalty: 'percentage-50', // String key for VSelect, converted to object on submit
    expirationTime: null,
  }],
  quantity: 1,
})

const isExternalSlot = computed(() => createForm.value.slotType === 'external')

const creating = ref(false)
const createError = ref(null)
const createSuccess = ref(null)

// Edit mode state
const isEditMode = ref(false)
const editingSlotId = ref(null)
const isViewingBookedSlot = ref(false)

// Restaurant autocomplete
const restaurantSearch = ref('')
const searchingRestaurants = ref(false)
const restaurantResults = ref([])

// Custom filter that strips diacritics so "cafe" matches "Café", etc.
const normalizedFilter = (itemTitle, queryText) => {
  if (!queryText) return true
  const normalize = s => s.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase()
  
  return normalize(itemTitle).includes(normalize(queryText))
}

watch(restaurantSearch, async newValue => {
  // For restaurant admins, filter from their own restaurants instead of searching
  if (!isPlatformAdmin.value) {
    if (!newValue || newValue.length < 2) {
      restaurantResults.value = myRestaurants.value

      return
    }

    // Filter myRestaurants by search term
    const searchLower = newValue.toLowerCase()
    restaurantResults.value = myRestaurants.value.filter(r =>
      r.name.toLowerCase().includes(searchLower) ||
      r.market?.toLowerCase().includes(searchLower)
    )

    return
  }

  // Platform admin: search all restaurants
  if (!newValue || newValue.length < 2) {
    restaurantResults.value = []

    return
  }

  searchingRestaurants.value = true
  try {
    await restaurantsStore.searchRestaurants(newValue)
    restaurantResults.value = restaurantsStore.restaurantSearchResults || []
  } catch (error) {
    console.error('Error searching restaurants:', error)
  } finally {
    searchingRestaurants.value = false
  }
})

// Initialize restaurant list for restaurant admins
watch(myRestaurants, newMyRestaurants => {
  if (!isPlatformAdmin.value && newMyRestaurants && newMyRestaurants.length > 0) {
    restaurantResults.value = newMyRestaurants
  }
}, { immediate: true })

// Watch create tab search results and fetch Google Places photos for items that need them
watch(
  () => restaurantResults.value,
  async newResults => {
    if (!newResults || newResults.length === 0) return

    // Fetch Google Places photos for all items that can fetch them
    newResults.forEach(item => {
      if (canFetchGooglePhotos(item)) {
        fetchGooglePlacePhoto(item)
      }
    })
  },
  { immediate: true }
)

const selectedRestaurant = computed(() => {
  if (!createForm.value.restaurantId) return null

  return restaurantResults.value.find(r => r._id === createForm.value.restaurantId)
})

const addSlotConfig = () => {
  createForm.value.slots.push({
    date: '2026-02-14',
    time: '19:30',
    partySize: 2,
    seatingArea: '',
    guaranteedSpendPerPerson: 200,
    taxPercentage: 12,
    tipPercentage: 20,
    ccFeePercentage: 0,
    paymentCaptureType: 'hold',
    cancellationPeriodHours: 24,
    penalty: 'percentage-50', // String key for VSelect
    expirationTime: null,
  })
}

const removeSlotConfig = index => {
  createForm.value.slots.splice(index, 1)
}

const cancelEdit = () => {
  isEditMode.value = false
  editingSlotId.value = null
  isViewingBookedSlot.value = false
  createError.value = null
  createSuccess.value = null

  // Reset form
  createForm.value = {
    restaurantId: null,
    slotType: null,
    externalBookingUrl: '',
    showGeneralAvailability: true,
    experienceName: 'Platinum Access',
    experienceDescription: 'This reservation is secured by a $xxxpp Food & Beverage minimum',
    experienceImageId: null,
    slots: [{
      date: '2026-02-14',
      time: '19:30',
      partySize: 2,
      seatingArea: '',
      guaranteedSpendPerPerson: 200,
      taxPercentage: 12,
      tipPercentage: 20,
      ccFeePercentage: 0,
      paymentCaptureType: 'hold',
      cancellationPeriodHours: 24,
      penalty: 'percentage-50', // String key for VSelect
      expirationTime: null,
    }],
    quantity: 1,
  }
  restaurantSearch.value = ''
}

const createSlots = async () => {
  createError.value = null
  createSuccess.value = null

  // Validation
  if (!createForm.value.restaurantId) {
    createError.value = 'Please select a restaurant'

    return
  }
  if (!createForm.value.experienceName) {
    createError.value = 'Please enter an event name'

    return
  }
  if (createForm.value.slots.length === 0) {
    createError.value = 'Please add at least one slot configuration'

    return
  }

  // External slot validation
  if (isExternalSlot.value) {
    if (!createForm.value.externalBookingUrl) {
      createError.value = 'Please enter an external booking URL'

      return
    }

    // Validate URL format
    try {
      new URL(createForm.value.externalBookingUrl)
    } catch {
      createError.value = 'Please enter a valid URL (e.g., https://resy.com/...)'

      return
    }
  }

  // Validate each slot
  for (let i = 0; i < createForm.value.slots.length; i++) {
    const slot = createForm.value.slots[i]
    if (!slot.date) {
      createError.value = `Slot ${i + 1}: Please select a date`

      return
    }
    if (!slot.time) {
      createError.value = `Slot ${i + 1}: Please select a time`

      return
    }

    // Payment field validation only for internal slots
    if (!isExternalSlot.value) {
      if (slot.guaranteedSpendPerPerson <= 0) {
        createError.value = `Slot ${i + 1}: Guaranteed spend must be greater than 0`

        return
      }
      if (slot.taxPercentage === null || slot.taxPercentage === undefined || slot.taxPercentage < 0) {
        createError.value = `Slot ${i + 1}: Please enter a valid tax percentage`

        return
      }
      if (slot.tipPercentage === null || slot.tipPercentage === undefined || slot.tipPercentage < 0) {
        createError.value = `Slot ${i + 1}: Please enter a valid tip percentage`

        return
      }
      if (slot.ccFeePercentage === null || slot.ccFeePercentage === undefined || slot.ccFeePercentage < 0) {
        createError.value = `Slot ${i + 1}: Please enter a valid CC fee percentage`

        return
      }
    }
  }

  creating.value = true

  try {
    if (isEditMode.value) {
      // Update existing slot
      const slot = createForm.value.slots[0]

      const updateData = {
        date: slot.date,
        time: slot.time,
        partySize: slot.partySize,
        seatingArea: slot.seatingArea,
        experienceName: createForm.value.experienceName,
        experienceDescription: createForm.value.experienceDescription,
        slotType: createForm.value.slotType,
        externalBookingUrl: createForm.value.externalBookingUrl,
        showGeneralAvailability: createForm.value.showGeneralAvailability,
      }

      // Pricing fields are always included (optional for external slots)
      updateData.guaranteedSpendPerPerson = slot.guaranteedSpendPerPerson
      updateData.taxPercentage = slot.taxPercentage
      updateData.tipPercentage = slot.tipPercentage
      updateData.ccFeePercentage = slot.ccFeePercentage

      if (!isExternalSlot.value) {
        updateData.paymentCaptureType = slot.paymentCaptureType
        updateData.cancellationPeriodHours = slot.cancellationPeriodHours
        updateData.penalty = keyToPenalty(slot.penalty)
        updateData.expirationTime = slot.expirationTime
      }

      await instantBookingSlotsStore.updateSlot(editingSlotId.value, updateData)

      createSuccess.value = 'Slot updated successfully!'

      // Reset edit mode and form
      cancelEdit()

      // Switch to manage tab
      setTimeout(() => {
        currentTab.value = 'manage'
        loadManageSlots()
      }, 1500)
    } else {
      // Create new slots - convert penalty string keys to objects
      const slotsWithEventInfo = createForm.value.slots.map(slot => {
        const slotData = {
          ...slot,
          experienceName: createForm.value.experienceName,
          experienceDescription: createForm.value.experienceDescription,
        }

        if (isExternalSlot.value) {
          slotData.slotType = 'external'
          slotData.externalBookingUrl = createForm.value.externalBookingUrl
          slotData.showGeneralAvailability = createForm.value.showGeneralAvailability
        } else {
          slotData.penalty = keyToPenalty(slot.penalty)
        }

        return slotData
      })

      const result = await instantBookingSlotsStore.createSlots({
        restaurantId: createForm.value.restaurantId,
        slots: slotsWithEventInfo,
        quantity: createForm.value.quantity,
      })

      createSuccess.value = `Successfully created ${result.slots.length} slots!`

      // Reset form
      createForm.value = {
        restaurantId: null,
        slotType: null,
        externalBookingUrl: '',
        showGeneralAvailability: true,
        experienceName: 'Platinum Access',
        experienceDescription: 'This reservation is secured by a $xxxpp Food & Beverage minimum',
        experienceImageId: null,
        slots: [{
          date: '2026-02-14',
          time: '19:30',
          partySize: 2,
          seatingArea: '',
          guaranteedSpendPerPerson: 200,
          taxPercentage: 12,
          tipPercentage: 20,
          ccFeePercentage: 0,
          paymentCaptureType: 'hold',
          cancellationPeriodHours: 24,
          penalty: 'percentage-50', // String key for VSelect
          expirationTime: null,
        }],
        quantity: 1,
      }
      restaurantSearch.value = ''

      // Switch to manage tab
      setTimeout(() => {
        currentTab.value = 'manage'
      }, 2000)
    }
  } catch (error) {
    console.error('Error saving slot:', error)
    createError.value = error.response?.data?.error || (isEditMode.value ? 'Failed to update slot' : 'Failed to create slots')
  } finally {
    creating.value = false
  }
}

// ============================================================================
// MANAGE SLOTS TAB
// ============================================================================

const manageRestaurantSearch = ref('')
const manageSelectedRestaurant = ref(null)
const manageStatusFilter = ref(null)
const manageDateFilter = ref(null)
const manageExperienceFilter = ref(null)

const manageRestaurantResults = ref([])
const searchingManageRestaurants = ref(false)

// Google Places service instance
const googlePlacesService = new GooglePlacesService()

// Cache for fetched Google Photos - reactive object for googlePlaceId -> photoUrl
const fetchedGooglePhotos = ref({})
const fetchingGooglePhotos = new Set()

// Track failed Google photo loads to skip cached URL and force a fresh fetch
const failedGooglePhotoIds = ref({})

// Helper to get Google Places photo URL
const getGooglePhotoUrl = (photoReference, maxWidth = 200, maxHeight = 200) => {
  const apiKey = import.meta.env.VITE_APP_GOOGLE_MAPS_API_KEY

  return `https://d128sk95sboo22.cloudfront.net/maps/api/place/photo?maxwidth=${maxWidth}&maxheight=${maxHeight}&photo_reference=${photoReference}&key=${apiKey}`
}

// Helper functions to check image availability for a restaurant item
const hasLocalImages = item => {
  const imageIds = item.imageIds ?? item.raw?.imageIds
  if (imageIds && imageIds.length > 0) return true

  const eventSpaces = item.eventSpaces ?? item.raw?.eventSpaces
  if (eventSpaces?.length > 0) {
    if (eventSpaces.some(space => space.imageId)) return true
  }

  return false
}

const hasCachedGooglePhotos = item => {
  const googlePlaceDetails = item.googlePlaceDetails ?? item.raw?.googlePlaceDetails

  return googlePlaceDetails?.photos?.length > 0 && googlePlaceDetails.photos[0].photo_reference
}

const canFetchGooglePhotos = item => {
  const googlePlaceId = item.googlePlaceId ?? item.raw?.googlePlaceId

  return googlePlaceId && !hasLocalImages(item) && !hasCachedGooglePhotos(item)
}

// Get restaurant image URL with fallback logic
const getRestaurantImageUrl = item => {
  const imageIds = item.imageIds ?? item.raw?.imageIds

  // 1. Use uploaded images from database
  if (imageIds?.length) {
    return mediaUrl(imageIds[0], 'thumb')
  }

  // 2. Fallback to event spaces images
  const eventSpaces = item.eventSpaces ?? item.raw?.eventSpaces
  if (eventSpaces?.length > 0) {
    const spaceWithImage = eventSpaces.find(space => space.imageId)
    if (spaceWithImage) {
      return mediaUrl(spaceWithImage.imageId, 'thumb')
    }
  }

  const googlePlaceId = item.googlePlaceId ?? item.raw?.googlePlaceId

  // 3. Fallback to cached Google Places photos (skip if previously failed to load)
  const googlePlaceDetails = item.googlePlaceDetails ?? item.raw?.googlePlaceDetails
  if (!failedGooglePhotoIds.value[googlePlaceId] && googlePlaceDetails?.photos?.length > 0) {
    const photoRef = googlePlaceDetails.photos[0].photo_reference
    if (photoRef) {
      return getGooglePhotoUrl(photoRef)
    }
  }

  // 4. Fallback to on-demand fetched Google Places photo
  if (googlePlaceId && fetchedGooglePhotos.value[googlePlaceId]) {
    return fetchedGooglePhotos.value[googlePlaceId]
  }

  return null
}

// Handle image load failure - attempt fresh Google Places fetch
const onImageError = item => {
  const googlePlaceId = item?.googlePlaceId ?? item?.raw?.googlePlaceId
  if (googlePlaceId && !fetchedGooglePhotos.value[googlePlaceId]) {
    failedGooglePhotoIds.value[googlePlaceId] = true
    fetchGooglePlacePhoto(item, { force: true })
  }
}

// Fetch Google Places photo on-demand
const fetchGooglePlacePhoto = async (item, { force = false } = {}) => {
  const googlePlaceId = item.googlePlaceId ?? item.raw?.googlePlaceId

  if (!googlePlaceId) return
  if (fetchingGooglePhotos.has(googlePlaceId)) return
  if (fetchedGooglePhotos.value[googlePlaceId]) return
  if (!force && !canFetchGooglePhotos(item)) return

  fetchingGooglePhotos.add(googlePlaceId)
  try {
    const placeDetails = await googlePlacesService.getPlaceDetails(googlePlaceId, ['photos'])
    if (placeDetails?.photos?.length > 0) {
      const photoRef = placeDetails.photos[0].photo_reference
      if (photoRef) {
        fetchedGooglePhotos.value[googlePlaceId] = getGooglePhotoUrl(photoRef)
      }
    }
  } catch (error) {
    console.error('Failed to fetch Google Places photo:', error)
  } finally {
    fetchingGooglePhotos.delete(googlePlaceId)
  }
}

// Check if restaurant has any image available
const hasRestaurantImage = item => {
  if (hasLocalImages(item)) return true
  if (hasCachedGooglePhotos(item)) return true

  const googlePlaceId = item.googlePlaceId ?? item.raw?.googlePlaceId
  if (googlePlaceId && fetchedGooglePhotos.value[googlePlaceId]) return true

  // Show image container if we can fetch (will show loading state)
  if (canFetchGooglePhotos(item)) return true

  return false
}

watch(manageRestaurantSearch, async newValue => {
  // For restaurant admins, filter from their own restaurants instead of searching
  if (!isPlatformAdmin.value) {
    if (!newValue || newValue.length < 2) {
      manageRestaurantResults.value = myRestaurants.value

      return
    }

    // Filter myRestaurants by search term
    const searchLower = newValue.toLowerCase()
    manageRestaurantResults.value = myRestaurants.value.filter(r =>
      r.name.toLowerCase().includes(searchLower) ||
      r.market?.toLowerCase().includes(searchLower)
    )

    return
  }

  // Platform admin: search all restaurants
  if (!newValue || newValue.length < 2) {
    manageRestaurantResults.value = []

    return
  }

  searchingManageRestaurants.value = true
  try {
    await restaurantsStore.searchRestaurants(newValue)
    manageRestaurantResults.value = restaurantsStore.restaurantSearchResults || []
  } catch (error) {
    console.error('Error searching restaurants:', error)
  } finally {
    searchingManageRestaurants.value = false
  }
})

// Initialize manage restaurant results for restaurant admins
watch(myRestaurants, newMyRestaurants => {
  if (!isPlatformAdmin.value && newMyRestaurants && newMyRestaurants.length > 0) {
    manageRestaurantResults.value = newMyRestaurants
  }
}, { immediate: true })

// Watch search results and fetch Google Places photos for items that need them
watch(
  () => manageRestaurantResults.value,
  async newResults => {
    if (!newResults || newResults.length === 0) return

    // Fetch Google Places photos for all items that can fetch them
    newResults.forEach(item => {
      if (canFetchGooglePhotos(item)) {
        fetchGooglePlacePhoto(item)
      }
    })
  },
  { immediate: true }
)

const loadManageSlots = async () => {
  try {
    const filters = {
      status: manageStatusFilter.value,
      date: manageDateFilter.value,
      experienceName: manageExperienceFilter.value,
    }

    if (manageSelectedRestaurant.value) {
      // Load slots for specific restaurant
      await instantBookingSlotsStore.fetchAdminRestaurantSlots(
        manageSelectedRestaurant.value._id,
        filters
      )
    } else if (!isPlatformAdmin.value && myRestaurantIds.value.length > 0) {
      // Restaurant admin without selected restaurant: load slots for their restaurants
      // Pass restaurant IDs as a filter
      await instantBookingSlotsStore.fetchAdminAllSlots({
        ...filters,
        restaurantIds: myRestaurantIds.value,
      })
    } else if (isPlatformAdmin.value) {
      // Platform admin: load all slots
      await instantBookingSlotsStore.fetchAdminAllSlots(filters)
    }
  } catch (error) {
    console.error('Error loading slots:', error)
  }
}

watch([manageSelectedRestaurant, manageStatusFilter, manageDateFilter, manageExperienceFilter], () => {
  loadManageSlots()
})

// Also reload when myRestaurantIds becomes available (for restaurant admins)
watch(myRestaurantIds, (newIds, oldIds) => {
  // Only load if we now have IDs and didn't before (initial load)
  if (newIds && newIds.length > 0 && (!oldIds || oldIds.length === 0)) {
    if (currentTab.value === 'manage') {
      loadManageSlots()
    }
    if (currentTab.value === 'analytics') {
      loadAnalytics()
    }
  }
})

const editSlot = slot => {
  // Set edit mode
  isEditMode.value = true
  editingSlotId.value = slot._id
  isViewingBookedSlot.value = slot.status === 'booked'
  createError.value = null
  createSuccess.value = null

  // Pre-populate restaurant in autocomplete
  const restaurantObj = {
    _id: slot.restaurantId,
    name: slot.restaurantName,
    market: slot.restaurantMarket,
  }
  restaurantResults.value = [restaurantObj]
  restaurantSearch.value = slot.restaurantName

  // Convert penalty object to string key for VSelect (fallback to default if not present)
  const penaltyKey = penaltyToKey(slot.penalty)

  // Populate the form with slot data
  createForm.value = {
    restaurantId: slot.restaurantId,
    slotType: slot.slotType || null,
    externalBookingUrl: slot.externalBookingUrl || '',
    showGeneralAvailability: slot.showGeneralAvailability !== undefined ? slot.showGeneralAvailability : true,
    experienceName: slot.experienceName || '',
    experienceDescription: slot.experienceDescription || 'This reservation is secured by a $xxxpp Food & Beverage minimum',
    experienceImageId: slot.experienceImageId || null,
    slots: [{
      date: slot.date ? moment.tz(slot.date, slot.restaurantTimeZone || 'America/Chicago').format('YYYY-MM-DD') : null,
      time: slot.time || '',
      partySize: slot.partySize || 2,
      seatingArea: slot.seatingArea || '',
      guaranteedSpendPerPerson: slot.guaranteedSpendPerPerson || 200,
      taxPercentage: slot.taxPercentage ?? 12,
      tipPercentage: slot.tipPercentage ?? 20,
      ccFeePercentage: slot.ccFeePercentage ?? 0,
      paymentCaptureType: slot.paymentCaptureType || 'hold',
      cancellationPeriodHours: slot.cancellationPeriodHours || 24,
      penalty: penaltyKey, // String key for VSelect
      expirationTime: slot.expirationTime ? moment.tz(slot.expirationTime, slot.restaurantTimeZone || 'America/Chicago').format('YYYY-MM-DDTHH:mm') : null,
    }],
    quantity: 1,
  }

  // Switch to create tab
  currentTab.value = 'create'
}

const cloneSlot = slot => {
  // Not edit mode - creating a new slot based on existing one
  isEditMode.value = false
  editingSlotId.value = null
  isViewingBookedSlot.value = false
  createError.value = null
  createSuccess.value = null

  // Pre-populate restaurant in autocomplete
  const restaurantObj = {
    _id: slot.restaurantId,
    name: slot.restaurantName,
    market: slot.restaurantMarket,
  }
  restaurantResults.value = [restaurantObj]
  restaurantSearch.value = slot.restaurantName

  // Convert penalty object to string key for VSelect (fallback to default if not present)
  const penaltyKey = penaltyToKey(slot.penalty)

  // Populate the form with slot data (excluding _id and status)
  createForm.value = {
    restaurantId: slot.restaurantId,
    slotType: slot.slotType || null,
    externalBookingUrl: slot.externalBookingUrl || '',
    showGeneralAvailability: slot.showGeneralAvailability !== undefined ? slot.showGeneralAvailability : true,
    experienceName: slot.experienceName || '',
    experienceDescription: slot.experienceDescription || 'This reservation is secured by a $xxxpp Food & Beverage minimum',
    experienceImageId: slot.experienceImageId || null,
    slots: [{
      date: slot.date ? moment.tz(slot.date, slot.restaurantTimeZone || 'America/Chicago').format('YYYY-MM-DD') : null,
      time: slot.time || '',
      partySize: slot.partySize || 2,
      seatingArea: slot.seatingArea || '',
      guaranteedSpendPerPerson: slot.guaranteedSpendPerPerson || 200,
      taxPercentage: slot.taxPercentage ?? 12,
      tipPercentage: slot.tipPercentage ?? 20,
      ccFeePercentage: slot.ccFeePercentage ?? 0,
      paymentCaptureType: slot.paymentCaptureType || 'hold',
      cancellationPeriodHours: slot.cancellationPeriodHours || 24,
      penalty: penaltyKey, // String key for VSelect
      expirationTime: slot.expirationTime ? moment.tz(slot.expirationTime, slot.restaurantTimeZone || 'America/Chicago').format('YYYY-MM-DDTHH:mm') : null,
    }],
    quantity: 1,
  }

  // Switch to create tab
  currentTab.value = 'create'
}


const deletingSlot = ref(null)
const showDeleteDialog = ref(false)

const deleteSlot = slot => {
  deletingSlot.value = slot
  showDeleteDialog.value = true
}

const confirmDeleteSlot = async () => {
  try {
    await instantBookingSlotsStore.deleteSlot(deletingSlot.value._id)
    showDeleteDialog.value = false
    await loadManageSlots()
  } catch (error) {
    console.error('Error deleting slot:', error)
    reportError('all-platinum-reserved.vue:confirmDeleteSlot', error.message, null, null, null, error)
  }
}

// Filtering is now done on the backend, so we can use slots directly

const uniqueExperienceNames = computed(() => {
  const names = new Set()
  slots.value.forEach(slot => {
    if (slot.experienceName) names.add(slot.experienceName)
  })

  return Array.from(names).sort()
})

// ============================================================================
// ANALYTICS TAB
// ============================================================================

const analyticsExperienceFilter = ref(null)

const loadAnalytics = async () => {
  try {
    await instantBookingSlotsStore.fetchAnalytics(analyticsExperienceFilter.value)
  } catch (error) {
    console.error('Error loading analytics:', error)
  }
}

watch(analyticsExperienceFilter, () => {
  loadAnalytics()
})

onMounted(() => {
  if (currentTab.value === 'analytics') {
    loadAnalytics()
  }
})

watch(currentTab, newTab => {
  if (newTab === 'analytics') {
    loadAnalytics()
  }
  if (newTab === 'manage') {
    loadManageSlots()
  }

  // Reset edit mode if navigating away from create tab
  if (newTab !== 'create' && isEditMode.value) {
    cancelEdit()
  }
})

const chartOptions = computed(() => {
  if (!analytics.value || !analytics.value.byRestaurant) return null

  const restaurants = analytics.value.byRestaurant.map(r => r.restaurantName)
  const booked = analytics.value.byRestaurant.map(r => r.booked)
  const available = analytics.value.byRestaurant.map(r => r.available)

  return {
    chart: {
      type: 'bar',
      stacked: true,
    },
    xaxis: {
      categories: restaurants,
    },
    dataLabels: {
      enabled: true,
    },
    plotOptions: {
      bar: {
        horizontal: false,
      },
    },
  }
})

const chartSeries = computed(() => {
  if (!analytics.value || !analytics.value.byRestaurant) return []

  return [
    {
      name: 'Booked',
      data: analytics.value.byRestaurant.map(r => r.booked),
    },
    {
      name: 'Available',
      data: analytics.value.byRestaurant.map(r => r.available),
    },
  ]
})

const calculateSlotTotal = slot => {
  const partySize = slot.partySize || 1
  const base = (slot.guaranteedSpendPerPerson || 0) * partySize
  const tax = base * (slot.taxPercentage || 0) / 100
  const tip = base * (slot.tipPercentage || 0) / 100
  const ccFee = (base + tax + tip) * (slot.ccFeePercentage || 0) / 100
  const total = base + tax + tip + ccFee

  return { base, tax, tip, ccFee, total }
}

const formatCurrency = amount => {
  const rounded = Math.round((amount + Number.EPSILON) * 100) / 100

  return `$${numberWithCommas(rounded.toFixed(2))}`
}

const formatDate = dateStr => {
  return moment(dateStr).format('ddd MMM D, YYYY')
}

const formatTime = timeStr => {
  if (!timeStr) return ''

  // Handle both HH:mm (24-hour) and H:mm (single digit hour) formats
  const parsed = moment(timeStr, ['HH:mm', 'H:mm'], true)
  if (!parsed.isValid()) {
    console.warn('Invalid time format:', timeStr)

    return timeStr
  }

  return parsed.format('h:mm A')
}

// Generate time options in 15-minute increments (12-hour format with AM/PM)
const timeOptions = computed(() => {
  const options = []
  for (let hour = 0; hour < 24; hour++) {
    for (let minute = 0; minute < 60; minute += 15) {
      const hourStr = hour.toString().padStart(2, '0')
      const minuteStr = minute.toString().padStart(2, '0')
      const value = `${hourStr}:${minuteStr}`

      // Convert to 12-hour format for display
      const hour12 = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
      const ampm = hour < 12 ? 'AM' : 'PM'
      const title = `${hour12}:${minuteStr} ${ampm}`

      options.push({ title, value })
    }
  }

  return options
})

// Party size options (1-20)
const partySizeOptions = computed(() => {
  const options = []
  for (let i = 1; i <= 20; i++) {
    options.push(i)
  }

  return options
})

// Spend per person options ($25 increments)
const spendPerPersonOptions = computed(() => {
  const options = []
  for (let i = 25; i <= 1000; i += 25) {
    options.push({ title: `$${i}`, value: i })
  }

  return options
})

// Cancellation/No Show Penalty options
// Using string keys as values for VSelect, with penaltyData containing the actual object
const penaltyOptions = computed(() => {
  return [
    { title: 'No penalty (full refund)', value: 'percentage-0' },
    { title: '$10 per person', value: 'fixed-10' },
    { title: '$25 per person', value: 'fixed-25' },
    { title: '$50 per person', value: 'fixed-50' },
    { title: '$100 per person', value: 'fixed-100' },
    { title: '50% of guaranteed min pp', value: 'percentage-50' },
    { title: '100% of guaranteed min pp', value: 'percentage-100' },
  ]
})

// Helper functions to convert between penalty objects and string keys for VSelect
const penaltyToKey = penalty => {
  if (!penalty || !penalty.type) return 'percentage-50'

  return `${penalty.type}-${penalty.amount}`
}

const keyToPenalty = key => {
  if (!key) return { type: 'percentage', amount: 50 }
  const [type, amountStr] = key.split('-')

  return { type, amount: Number(amountStr) }
}

// Tax percentage options (0-20%)
const taxPercentageOptions = computed(() => {
  const options = [0]
  for (let i = 1; i <= 20; i += 1) {
    options.push(i)
  }

  return options
})

// Tip percentage options (start at 20%, 5% increments)
const tipPercentageOptions = computed(() => {
  const options = []
  for (let i = 20; i <= 50; i += 5) {
    options.push({ title: `${i}`, value: i })
  }

  return options
})

// CC Fee percentage options (0-5%)
const ccFeePercentageOptions = computed(() => {
  const options = []
  for (let i = 0; i <= 10; i++) {
    const value = i * 0.5
    options.push({ title: `${value}`, value: value })
  }

  return options
})

// Calculate default expiration time based on date, time, and cancellation period
const calculateDefaultExpirationTime = slot => {
  if (!slot.date || !slot.time || !slot.cancellationPeriodHours) {
    return null
  }

  const dateTimeString = `${slot.date} ${slot.time}`
  const bookingDateTime = moment(dateTimeString, 'YYYY-MM-DD HH:mm')

  if (!bookingDateTime.isValid()) {
    return null
  }

  return bookingDateTime.subtract(slot.cancellationPeriodHours, 'hours').format('YYYY-MM-DDTHH:mm')
}

// Watch for changes to date/time/cancellationPeriodHours and auto-update expirationTime
watch(() => createForm.value.slots, newSlots => {
  newSlots.forEach(slot => {
    // Only auto-calculate if expirationTime is not manually set
    if (!slot.expirationTimeManuallySet) {
      const calculatedExpiration = calculateDefaultExpirationTime(slot)
      if (calculatedExpiration && slot.expirationTime !== calculatedExpiration) {
        slot.expirationTime = calculatedExpiration
      }
    }
  })
}, { deep: true })
</script>

<template>
  <VCard>
    <VCardTitle class="d-flex align-center justify-space-between">
      <span>Instant Bookings Admin</span>
    </VCardTitle>

    <VCardText>
      <VTabs v-model="currentTab">
        <VTab value="create">
          {{ isViewingBookedSlot ? 'View Slot' : (isEditMode ? 'Edit Slot' : 'Create Slots') }}
        </VTab>
        <VTab value="manage">
          Manage Slots
        </VTab>
        <VTab value="analytics">
          Analytics
        </VTab>
      </VTabs>

      <VWindow
        v-model="currentTab"
        class="mt-8"
      >
        <!-- ================================================================ -->
        <!-- CREATE SLOTS TAB -->
        <!-- ================================================================ -->
        <VWindowItem value="create">
          <VForm @submit.prevent="createSlots">
            <VAlert
              v-if="createError"
              type="error"
              class="mb-4"
              closable
              @click:close="createError = null"
            >
              {{ createError }}
            </VAlert>

            <VAlert
              v-if="createSuccess"
              type="success"
              class="mb-4"
              closable
              @click:close="createSuccess = null"
            >
              {{ createSuccess }}
            </VAlert>

            <!-- Restaurant Selection -->
            <VRow class="mt-6">
              <VCol
                cols="12"
                md="6"
              >
                <VAutocomplete
                  v-model="createForm.restaurantId"
                  v-model:search="restaurantSearch"
                  :items="restaurantResults"
                  :loading="searchingRestaurants"
                  :disabled="isViewingBookedSlot"
                  :custom-filter="normalizedFilter"
                  item-title="name"
                  item-value="_id"
                  label="Restaurant *"
                  placeholder="Search for a restaurant..."
                  clearable
                  required
                >
                  <template #item="{ props, item }">
                    <VListItem
                      v-bind="props"
                      title=""
                    >
                      <div class="search-dropdown-card">
                        <div class="search-dropdown-content">
                          <div class="search-dropdown-title-wrapper">
                            <span class="search-dropdown-title">{{ item.raw.name }}</span>
                          </div>
                          <div class="search-dropdown-subtitle-wrapper">
                            <p
                              v-if="item.raw.market"
                              class="search-dropdown-subtitle"
                            >
                              {{ item.raw.market }}
                            </p>
                          </div>
                        </div>

                        <div class="search-dropdown-image-container">
                          <VImg
                            v-if="hasRestaurantImage(item.raw) && getRestaurantImageUrl(item.raw)"
                            :src="getRestaurantImageUrl(item.raw)"
                            class="restaurant-image rounded-lg"
                            cover
                            height="60"
                            width="60"
                            @error="onImageError(item.raw)"
                          >
                            <template #placeholder>
                              <VRow
                                class="fill-height ma-0"
                                align="center"
                                justify="center"
                              >
                                <VProgressCircular
                                  indeterminate
                                  color="grey-lighten-5"
                                  size="20"
                                />
                              </VRow>
                            </template>
                          </VImg>
                          <div
                            v-else-if="canFetchGooglePhotos(item.raw)"
                            class="image-placeholder"
                          >
                            <VRow
                              class="fill-height ma-0"
                              align="center"
                              justify="center"
                            >
                              <VProgressCircular
                                indeterminate
                                color="grey-lighten-5"
                                size="20"
                              />
                            </VRow>
                          </div>
                        </div>
                      </div>
                    </VListItem>
                  </template>
                </VAutocomplete>
              </VCol>
            </VRow>

            <!-- Slot Type Selection -->
            <VRow>
              <VCol
                cols="12"
                md="6"
              >
                <VSelect
                  v-model="createForm.slotType"
                  :items="[
                    { title: 'RM Only', value: null },
                    { title: 'External', value: 'external' },
                  ]"
                  label="Checkout Location"
                  :disabled="isViewingBookedSlot || isEditMode"
                />
              </VCol>
            </VRow>

            <!-- External Slot Fields -->
            <VRow v-if="isExternalSlot">
              <VCol
                cols="12"
                md="6"
              >
                <VTextField
                  v-model="createForm.externalBookingUrl"
                  label="External Booking URL *"
                  placeholder="https://resy.com/cities/ny/restaurant..."
                  :disabled="isViewingBookedSlot"
                  required
                />
              </VCol>
              <VCol
                cols="12"
                md="6"
              >
                <VSwitch
                  v-model="createForm.showGeneralAvailability"
                  label="Show general availability link"
                  :disabled="isViewingBookedSlot"
                  color="primary"
                  hide-details
                />
              </VCol>
            </VRow>

            <!-- Experience Details -->
            <VRow>
              <VCol
                cols="12"
                md="6"
              >
                <VTextField
                  v-model="createForm.experienceName"
                  label="Experience Name *"
                  placeholder="Valentine's Day 2026"
                  :disabled="isViewingBookedSlot"
                  required
                />
              </VCol>
              <VCol cols="12">
                <VuetifyTiptap
                  v-model="createForm.experienceDescription"
                  label="Experience Description"
                  placeholder="Optional description"
                  :output="tiptapOutput"
                  :outlined="tiptapOutlined"
                  :max-width="tiptapMaxWidth"
                  :max-height="300"
                  :disabled="isViewingBookedSlot"
                  rounded
                />
              </VCol>
            </VRow>

            <VDivider class="my-4" />

            <!-- Slot Configurations -->
            <div
              v-for="(slot, index) in createForm.slots"
              :key="index"
              class="mb-6"
            >
              <div class="d-flex align-center justify-space-between mb-2">
                <VBtn
                  v-if="createForm.slots.length > 1"
                  icon="mdi-close"
                  size="small"
                  variant="text"
                  @click="removeSlotConfig(index)"
                />
              </div>
              <VRow v-if="!isExternalSlot">
                <VCol
                  cols="12"
                  md="9"
                >
                  <VTextField
                    v-model="slot.seatingArea"
                    label="Seating Type (optional)"
                    placeholder="Main Dining Room"
                    :disabled="isViewingBookedSlot"
                  />
                </VCol>
              </VRow>
              <VRow class="mb-4">
                <VCol
                  cols="12"
                  md="9"
                >
                  <div class="d-flex datetime-picker-group">
                    <div class="datetime-picker-field">
                      <span class="datetime-label">Date</span>
                      <AppDateTimePicker
                        v-model="slot.date"
                        :config="{ disableMobile: true, altInput: true, altFormat: 'D n/j', dateFormat: 'Y-m-d', maxDate: moment().add(1, 'year').format('YYYY-MM-DD') }"
                        :rules="[requiredValidator]"
                        :disabled="isViewingBookedSlot"
                        required
                        placeholder="2/14"
                        density="compact"
                        variant="outlined"
                        hide-details
                      />
                    </div>
                    <div class="datetime-picker-field">
                      <span class="datetime-label">Time</span>
                      <VSelect
                        v-model="slot.time"
                        :items="timeOptions"
                        :disabled="isViewingBookedSlot || !slot.date"
                        placeholder="7:30 PM"
                        density="compact"
                        variant="outlined"
                        hide-details
                        required
                      />
                    </div>
                    <div class="datetime-picker-field">
                      <span class="datetime-label">Party Size</span>
                      <VSelect
                        v-model="slot.partySize"
                        :items="partySizeOptions"
                        :disabled="isViewingBookedSlot"
                        placeholder="2"
                        density="compact"
                        variant="outlined"
                        hide-details
                        required
                      />
                    </div>
                  </div>
                </VCol>
              </VRow>



              <!-- Pricing fields -->
              <VRow>
                <VCol
                  cols="12"
                  md="3"
                >
                  <VTextField
                    v-model.number="slot.guaranteedSpendPerPerson"
                    type="text"
                    label="Spend Per Person ($)"
                    :disabled="isViewingBookedSlot"
                    clearable
                  />
                </VCol>
                <VCol
                  cols="12"
                  md="3"
                >
                  <VTextField
                    v-model.number="slot.taxPercentage"
                    type="text"
                    label="Tax %"
                    :disabled="isViewingBookedSlot"
                    suffix="%"
                    :required="!isExternalSlot"
                  />
                </VCol>
                <VCol
                  cols="12"
                  md="3"
                >
                  <VTextField
                    v-model.number="slot.tipPercentage"
                    type="text"
                    label="Tip %"
                    :disabled="isViewingBookedSlot"
                    suffix="%"
                    :required="!isExternalSlot"
                  />
                </VCol>
                <VCol
                  cols="12"
                  md="3"
                >
                  <VTextField
                    v-model.number="slot.ccFeePercentage"
                    type="text"
                    label="CC Charge %"
                    :disabled="isViewingBookedSlot"
                    suffix="%"
                    :required="!isExternalSlot"
                  />
                </VCol>
              </VRow>

              <!-- Total Calculation -->
              <VRow v-if="slot.guaranteedSpendPerPerson > 0">
                <VCol
                  cols="12"
                  md="12"
                >
                  <div class="total-calculation-card pa-4 rounded-lg">
                    <div class="d-flex flex-column gap-1">
                      <div class="d-flex justify-space-between text-body-2">
                        <span>Subtotal ({{ formatCurrency(slot.guaranteedSpendPerPerson) }} x {{ slot.partySize || 1 }})</span>
                        <span>{{ formatCurrency(slot.guaranteedSpendPerPerson * (slot.partySize || 1)) }}</span>
                      </div>
                      <div
                        v-if="slot.taxPercentage > 0"
                        class="d-flex justify-space-between text-body-2 text-medium-emphasis"
                      >
                        <span>Tax ({{ slot.taxPercentage }}%)</span>
                        <span>{{ formatCurrency(calculateSlotTotal(slot).tax) }}</span>
                      </div>
                      <div
                        v-if="slot.tipPercentage > 0"
                        class="d-flex justify-space-between text-body-2 text-medium-emphasis"
                      >
                        <span>Tip ({{ slot.tipPercentage }}%)</span>
                        <span>{{ formatCurrency(calculateSlotTotal(slot).tip) }}</span>
                      </div>
                      <div
                        v-if="slot.ccFeePercentage > 0"
                        class="d-flex justify-space-between text-body-2 text-medium-emphasis"
                      >
                        <span>CC Fee ({{ slot.ccFeePercentage }}%)</span>
                        <span>{{ formatCurrency(calculateSlotTotal(slot).ccFee) }}</span>
                      </div>
                      <VDivider class="my-1" />
                      <div class="d-flex justify-space-between text-body-1 font-weight-bold">
                        <span>Total</span>
                        <span>{{ formatCurrency(calculateSlotTotal(slot).total) }}</span>
                      </div>
                    </div>
                  </div>
                </VCol>
              </VRow>

              <!-- Payment/Policy fields (hidden for external slots) -->
              <template v-if="!isExternalSlot">
                <VRow>
                  <VCol
                    cols="12"
                    md="4"
                  >
                    <VSelect
                      v-model="slot.paymentCaptureType"
                      :items="[
                        { title: 'CC on hold', value: 'hold' },
                        { title: 'Upfront payment', value: 'immediate' },
                      ]"
                      label="Payment Type *"
                      :disabled="isViewingBookedSlot"
                      required
                    />
                  </VCol>
                  <VCol
                    cols="12"
                    md="4"
                  >
                    <VSelect
                      v-model.number="slot.cancellationPeriodHours"
                      :items="[
                        { title: '6 hours', value: 6 },
                        { title: '12 hours', value: 12 },
                        { title: '24 hours', value: 24 },
                        { title: '48 hours', value: 48 },
                        { title: '1 week', value: 168 },
                      ]"
                      label="Cancellation Period *"
                      :disabled="isViewingBookedSlot"
                      required
                    />
                  </VCol>
                  <VCol
                    cols="12"
                    md="4"
                  >
                    <VSelect
                      v-model="slot.penalty"
                      :items="penaltyOptions"
                      item-title="title"
                      item-value="value"
                      label="Cancellation/No Show Penalty *"
                      :disabled="isViewingBookedSlot"
                      required
                    />
                  </VCol>
                </VRow>

                <VRow>
                  <VCol
                    cols="12"
                    md="9"
                  >
                    <div class="d-flex align-center gap-2">
                      <div class="flex-grow-1">
                        <span class="text-caption text-medium-emphasis d-block mb-1">Expiration Date/Time *</span>
                        <div class="d-flex datetime-picker-group">
                          <div class="datetime-picker-field">
                            <span class="datetime-label">Date</span>
                            <AppDateTimePicker
                              :model-value="slot.expirationTime ? slot.expirationTime.split('T')[0] : null"
                              :config="{ disableMobile: true, altInput: true, altFormat: 'D n/j', dateFormat: 'Y-m-d', maxDate: moment().add(1, 'year').format('YYYY-MM-DD') }"
                              :disabled="isViewingBookedSlot"
                              placeholder="2/13"
                              density="compact"
                              variant="outlined"
                              hide-details
                              @update:model-value="(val) => { slot.expirationTime = val + 'T' + (slot.expirationTime?.split('T')[1] || '00:00'); slot.expirationTimeManuallySet = true; }"
                            />
                          </div>
                          <div class="datetime-picker-field">
                            <span class="datetime-label">Time</span>
                            <VSelect
                              :model-value="slot.expirationTime ? slot.expirationTime.split('T')[1] : null"
                              :items="timeOptions"
                              :disabled="isViewingBookedSlot"
                              placeholder="7:30 PM"
                              density="compact"
                              variant="outlined"
                              hide-details
                              @update:model-value="(val) => { slot.expirationTime = (slot.expirationTime?.split('T')[0] || '') + 'T' + val; slot.expirationTimeManuallySet = true; }"
                            />
                          </div>
                        </div>
                      </div>
                      <VBtn
                        v-if="slot.expirationTimeManuallySet && !isViewingBookedSlot"
                        icon="mdi-refresh"
                        size="small"
                        variant="text"
                        title="Reset to auto-calculated time"
                        @click="slot.expirationTime = calculateDefaultExpirationTime(slot); slot.expirationTimeManuallySet = false"
                      />
                    </div>
                    <div class="text-caption text-medium-emphasis mt-1">
                      Auto-calculated as booking date/time minus cancellation period. Can be manually adjusted.
                    </div>
                  </VCol>
                </VRow>
              </template>

              <VDivider class="my-4" />
            </div>

            <VBtn
              v-if="!isEditMode"
              color="secondary"
              variant="outlined"
              prepend-icon="mdi-plus"
              class="mb-4"
              @click="addSlotConfig"
            >
              Add Another Booking Slot
            </VBtn>
            <!-- Quantity (only show when creating, not editing) -->
            <VRow v-if="!isEditMode">
              <VCol
                cols="12"
                md="4"
              >
                <VTextField
                  v-model.number="createForm.quantity"
                  type="number"
                  label="Quantity (per configuration) *"
                  hint="How many identical slots to create for each configuration"
                  persistent-hint
                  min="1"
                  required
                />
              </VCol>
            </VRow>

            <div class="mt-6 d-flex gap-2">
              <VBtn
                v-if="!isViewingBookedSlot"
                type="submit"
                color="primary"
                :loading="creating"
                :disabled="creating"
              >
                {{ isEditMode ? 'Update Slot' : `Create ${createForm.quantity * createForm.slots.length} Slots` }}
              </VBtn>
              <VBtn
                v-if="isEditMode"
                variant="outlined"
                :disabled="creating"
                @click="cancelEdit"
              >
                {{ isViewingBookedSlot ? 'Close' : 'Cancel' }}
              </VBtn>
            </div>
          </VForm>
        </VWindowItem>

        <!-- ================================================================ -->
        <!-- MANAGE SLOTS TAB -->
        <!-- ================================================================ -->
        <VWindowItem value="manage">
          <!-- Filters -->
          <VRow class="mt-6 mb-4">
            <VCol
              cols="12"
              md="4"
            >
              <VAutocomplete
                v-model="manageSelectedRestaurant"
                v-model:search="manageRestaurantSearch"
                :items="manageRestaurantResults"
                :loading="searchingManageRestaurants"
                :custom-filter="normalizedFilter"
                item-title="name"
                item-value="_id"
                label="Restaurant"
                placeholder="Search for a restaurant..."
                clearable
                return-object
              >
                <template #item="{ props, item }">
                  <VListItem
                    v-bind="props"
                    title=""
                  >
                    <div class="search-dropdown-card">
                      <div class="search-dropdown-content">
                        <div class="search-dropdown-title-wrapper">
                          <span class="search-dropdown-title">{{ item.raw.name }}</span>
                        </div>
                        <div class="search-dropdown-subtitle-wrapper">
                          <p
                            v-if="item.raw.market"
                            class="search-dropdown-subtitle"
                          >
                            {{ item.raw.market }}
                          </p>
                        </div>
                      </div>

                      <div class="search-dropdown-image-container">
                        <VImg
                          v-if="hasRestaurantImage(item.raw) && getRestaurantImageUrl(item.raw)"
                          :src="getRestaurantImageUrl(item.raw)"
                          class="restaurant-image rounded-lg"
                          cover
                          height="60"
                          width="60"
                          @error="onImageError(item.raw)"
                        >
                          <template #placeholder>
                            <VRow
                              class="fill-height ma-0"
                              align="center"
                              justify="center"
                            >
                              <VProgressCircular
                                indeterminate
                                color="grey-lighten-5"
                                size="20"
                              />
                            </VRow>
                          </template>
                        </VImg>
                        <div
                          v-else-if="canFetchGooglePhotos(item.raw)"
                          class="image-placeholder"
                        >
                          <VRow
                            class="fill-height ma-0"
                            align="center"
                            justify="center"
                          >
                            <VProgressCircular
                              indeterminate
                              color="grey-lighten-5"
                              size="20"
                            />
                          </VRow>
                        </div>
                      </div>
                    </div>
                  </VListItem>
                </template>
              </VAutocomplete>
            </VCol>
            <VCol
              cols="12"
              md="3"
            >
              <VSelect
                v-model="manageStatusFilter"
                :items="[
                  { title: 'All Statuses', value: null },
                  { title: 'Available', value: 'available' },
                  { title: 'Booked', value: 'booked' },
                  { title: 'Canceled', value: 'canceled' },
                ]"
                label="Status"
                clearable
              />
            </VCol>
            <VCol
              cols="12"
              md="3"
            >
              <AppDateTimePicker
                v-model="manageDateFilter"
                label="Date"
                :config="{ disableMobile: true, altInput: true, altFormat: 'l, n/j', dateFormat: 'Y-m-d' }"
                clearable
              />
            </VCol>
            <VCol
              cols="12"
              md="2"
            >
              <VSelect
                v-model="manageExperienceFilter"
                :items="[{ title: 'All Experiences', value: null }, ...uniqueExperienceNames.map(name => ({ title: name, value: name }))]"
                label="Experience"
                clearable
              />
            </VCol>
          </VRow>

          <!-- Slots Table - Desktop -->
          <VDataTable
            v-if="!$vuetify.display.mobile"
            :items="slots"
            :loading="loading"
            :headers="[
              { title: 'Restaurant', key: 'restaurantName' },
              { title: 'Experience', key: 'experienceName' },
              { title: 'Type', key: 'slotType' },
              { title: 'Date', key: 'date' },
              { title: 'Time', key: 'time' },
              { title: 'Party Size', key: 'partySize' },
              { title: 'Price Per Person', key: 'guaranteedSpendPerPerson' },
              { title: 'Status', key: 'status' },
              { title: 'Actions', key: 'actions', sortable: false },
            ]"
          >
            <template #item.slotType="{ item }">
              <VChip
                :color="item.slotType === 'external' ? 'warning' : 'info'"
                size="small"
              >
                {{ item.slotType === 'external' ? 'External' : 'Internal' }}
              </VChip>
            </template>

            <template #item.date="{ item }">
              {{ formatDate(item.date) }}
            </template>

            <template #item.time="{ item }">
              {{ formatTime(item.time) }}
            </template>

            <template #item.guaranteedSpendPerPerson="{ item }">
              {{ item.guaranteedSpendPerPerson ? formatCurrency(item.guaranteedSpendPerPerson) : '—' }}
            </template>

            <template #item.status="{ item }">
              <VChip
                :color="item.status === 'available' ? 'success' : item.status === 'booked' ? 'primary' : 'error'"
                size="small"
              >
                {{ item.status }}
              </VChip>
            </template>

            <template #item.actions="{ item }">
              <VBtn
                :icon="item.status === 'booked' ? 'mdi-eye' : 'mdi-pencil'"
                size="small"
                variant="text"
                @click="editSlot(item)"
              />
              <VBtn
                icon="mdi-content-copy"
                size="small"
                variant="text"
                title="Clone slot"
                @click="cloneSlot(item)"
              />
              <VBtn
                icon="mdi-delete"
                size="small"
                variant="text"
                :disabled="item.status === 'booked'"
                @click="deleteSlot(item)"
              />
            </template>
          </VDataTable>

          <!-- Slots Cards - Mobile -->
          <div v-else>
            <VProgressLinear
              v-if="loading"
              indeterminate
              color="primary"
              class="mb-4"
            />

            <div
              v-if="!loading && slots.length === 0"
              class="text-center py-8 text-medium-emphasis"
            >
              No slots found
            </div>

            <VCard
              v-for="slot in slots"
              :key="slot._id"
              class="mb-3 slot-card-mobile"
            >
              <VCardText>
                <div class="d-flex justify-space-between align-start mb-3">
                  <div class="flex-grow-1">
                    <div class="text-h6 mb-1">
                      {{ slot.restaurantName }}
                    </div>
                    <div class="text-caption text-medium-emphasis">
                      {{ slot.experienceName }}
                    </div>
                  </div>
                  <div class="d-flex gap-2">
                    <VChip
                      :color="slot.slotType === 'external' ? 'warning' : 'info'"
                      size="small"
                    >
                      {{ slot.slotType === 'external' ? 'External' : 'Internal' }}
                    </VChip>
                    <VChip
                      :color="slot.status === 'available' ? 'success' : slot.status === 'booked' ? 'primary' : 'error'"
                      size="small"
                    >
                      {{ slot.status }}
                    </VChip>
                  </div>
                </div>

                <VDivider class="my-3" />

                <div class="slot-details-grid">
                  <div class="slot-detail-item">
                    <div class="slot-detail-label">
                      Date
                    </div>
                    <div class="slot-detail-value">
                      {{ formatDate(slot.date) }}
                    </div>
                  </div>
                  <div class="slot-detail-item">
                    <div class="slot-detail-label">
                      Time
                    </div>
                    <div class="slot-detail-value">
                      {{ formatTime(slot.time) }}
                    </div>
                  </div>
                  <div class="slot-detail-item">
                    <div class="slot-detail-label">
                      Party Size
                    </div>
                    <div class="slot-detail-value">
                      {{ slot.partySize }}
                    </div>
                  </div>
                  <div class="slot-detail-item">
                    <div class="slot-detail-label">
                      Price Per Person
                    </div>
                    <div class="slot-detail-value">
                      {{ slot.guaranteedSpendPerPerson ? formatCurrency(slot.guaranteedSpendPerPerson) : '—' }}
                    </div>
                  </div>
                </div>
              </VCardText>

              <VCardActions class="px-4 pb-4 pt-0">
                <VBtn
                  variant="tonal"
                  color="primary"
                  size="small"
                  :prepend-icon="slot.status === 'booked' ? 'mdi-eye' : 'mdi-pencil'"
                  @click="editSlot(slot)"
                >
                  {{ slot.status === 'booked' ? 'View' : 'Edit' }}
                </VBtn>
                <VBtn
                  variant="tonal"
                  color="secondary"
                  size="small"
                  prepend-icon="mdi-content-copy"
                  @click="cloneSlot(slot)"
                >
                  Clone
                </VBtn>
                <VBtn
                  variant="tonal"
                  color="error"
                  size="small"
                  prepend-icon="mdi-delete"
                  :disabled="slot.status === 'booked'"
                  @click="deleteSlot(slot)"
                >
                  Delete
                </VBtn>
              </VCardActions>
            </VCard>
          </div>
        </VWindowItem>

        <!-- ================================================================ -->
        <!-- ANALYTICS TAB -->
        <!-- ================================================================ -->
        <VWindowItem value="analytics">
          <!-- Experience Filter -->
          <VRow class="mt-6 mb-4">
            <VCol
              cols="12"
              md="4"
            >
              <VSelect
                v-model="analyticsExperienceFilter"
                :items="[{ title: 'All Experiences', value: null }, ...uniqueExperienceNames.map(name => ({ title: name, value: name }))]"
                label="Experience"
                clearable
              />
            </VCol>
          </VRow>

          <!-- Metrics Cards -->
          <VRow v-if="analytics">
            <VCol
              cols="12"
              md="3"
            >
              <VCard>
                <VCardText>
                  <div class="text-caption text-medium-emphasis">
                    Total Slots
                  </div>
                  <div class="text-h4">
                    {{ analytics.totalSlots }}
                  </div>
                </VCardText>
              </VCard>
            </VCol>
            <VCol
              cols="12"
              md="3"
            >
              <VCard>
                <VCardText>
                  <div class="text-caption text-medium-emphasis">
                    Booked
                  </div>
                  <div class="text-h4 text-primary">
                    {{ analytics.bookedSlots }}
                  </div>
                </VCardText>
              </VCard>
            </VCol>
            <VCol
              cols="12"
              md="3"
            >
              <VCard>
                <VCardText>
                  <div class="text-caption text-medium-emphasis">
                    Available
                  </div>
                  <div class="text-h4 text-success">
                    {{ analytics.availableSlots }}
                  </div>
                </VCardText>
              </VCard>
            </VCol>
            <VCol
              cols="12"
              md="3"
            >
              <VCard>
                <VCardText>
                  <div class="text-caption text-medium-emphasis">
                    Total Revenue
                  </div>
                  <div class="text-h4 text-success">
                    {{ formatCurrency(analytics.totalRevenue) }}
                  </div>
                </VCardText>
              </VCard>
            </VCol>
          </VRow>

          <!-- Chart -->
          <VCard
            v-if="analytics && chartOptions"
            class="mt-4"
          >
            <VCardTitle>Bookings by Restaurant</VCardTitle>
            <VCardText>
              <VueApexCharts
                type="bar"
                :options="chartOptions"
                :series="chartSeries"
                height="400"
              />
            </VCardText>
          </VCard>

          <!-- Breakdown Table -->
          <VCard
            v-if="analytics"
            class="mt-4"
          >
            <VCardTitle>Breakdown by Restaurant</VCardTitle>
            <VCardText>
              <VDataTable
                :items="analytics.byRestaurant"
                :headers="[
                  { title: 'Restaurant', key: 'restaurantName' },
                  { title: 'Booked', key: 'booked' },
                  { title: 'Available', key: 'available' },
                  { title: 'Total', value: (item) => item.booked + item.available },
                ]"
              >
                <template #item.booked="{ item }">
                  <VChip
                    color="primary"
                    size="small"
                  >
                    {{ item.booked }}
                  </VChip>
                </template>
                <template #item.available="{ item }">
                  <VChip
                    color="success"
                    size="small"
                  >
                    {{ item.available }}
                  </VChip>
                </template>
              </VDataTable>
            </VCardText>
          </VCard>
        </VWindowItem>
      </VWindow>
    </VCardText>
  </VCard>

  <!-- Comms history panel: admin view for selected restaurant. -->
  <VCard
    v-if="isPlatformAdmin && createForm.restaurantId"
    class="mt-4 pa-4"
  >
    <div class="text-subtitle-1 mb-2">
      Comms History — {{ selectedRestaurant?.name || 'Selected Restaurant' }}
    </div>
    <CommsHistoryPanel :restaurant-id="createForm.restaurantId" />
  </VCard>

  <!-- Delete Confirmation Dialog -->
  <VDialog
    v-model="showDeleteDialog"
    max-width="400"
  >
    <VCard>
      <VCardTitle>Confirm Delete</VCardTitle>
      <VCardText>
        Are you sure you want to delete this slot? This action cannot be undone.
      </VCardText>
      <VCardActions>
        <VSpacer />
        <VBtn @click="showDeleteDialog = false">
          Cancel
        </VBtn>
        <VBtn
          color="error"
          @click="confirmDeleteSlot"
        >
          Delete
        </VBtn>
      </VCardActions>
    </VCard>
  </VDialog>
</template>

<style scoped>
/* Datetime picker group - unified appearance */
.datetime-picker-group {
  gap: 0;
  border: 1px solid rgba(0, 0, 0, 0.23);
  border-radius: 4px;
  overflow: hidden;
}

.datetime-picker-field {
  flex: 1;
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 8px;
  overflow: hidden;
}

.datetime-picker-field:nth-child(1) {
  flex: 1;
}

.datetime-picker-field:nth-child(2) {
  flex: 1.2;
}

.datetime-picker-field:nth-child(3) {
  flex: 0.8;
}

.datetime-label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  white-space: nowrap;
  font-weight: 500;
  margin-bottom: 4px;
}

.datetime-picker-field:not(:last-child)::after {
  content: '';
  position: absolute;
  right: 0;
  top: 8px;
  bottom: 8px;
  width: 1px;
  background-color: rgba(0, 0, 0, 0.12);
  z-index: 1;
}

.datetime-picker-field :deep(.v-input) {
  border-radius: 0;
  height: 32px;
}

.datetime-picker-field :deep(.v-input__details) {
  display: none !important;
}

.datetime-picker-field :deep(.v-field) {
  border: none !important;
  box-shadow: none !important;
}

.datetime-picker-field :deep(.v-field__outline) {
  display: none;
}

.datetime-picker-field :deep(.v-input__control) {
  min-height: 32px;
  height: 32px;
}

.datetime-picker-field :deep(.v-field) {
  height: 32px;
  min-height: 32px;
  padding-top: 0;
  padding-bottom: 0;
}

.datetime-picker-field :deep(.v-field__input) {
  font-size: 16px;
  padding-top: 0;
  padding-bottom: 0;
  padding-left: 8px;
  padding-right: 4px;
  min-height: 32px;
  height: 32px;
  display: flex;
  align-items: center;
}

.datetime-picker-field :deep(.v-field__append-inner) {
  padding-top: 0;
  padding-bottom: 0;
  align-items: center;
  display: flex;
  height: 32px;
}

.datetime-picker-field :deep(.v-select__selection-text),
.datetime-picker-field :deep(.flatpickr-input) {
  font-size: 16px !important;
}

/* Total calculation card */
.total-calculation-card {
  background-color: rgba(var(--v-theme-surface-variant), 0.08);
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.v-data-table {
  margin-top: 1rem;
}

/* Restaurant search dropdown styles */
.search-dropdown-card {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  gap: 12px;
  cursor: pointer;
  transition: background-color 0.2s;
  min-height: 72px;
}

.search-dropdown-card:hover {
  background-color: rgba(0, 0, 0, 0.02);
}

.search-dropdown-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow: hidden;
}

.search-dropdown-title-wrapper {
  flex: 1;
  min-width: 0;
  display: inline-block;
}

.search-dropdown-title {
  font-weight: 500;
  font-size: 15px;
  line-height: 1.4;
  color: var(--ds-text-strong);
  word-wrap: break-word;
  overflow-wrap: break-word;
  hyphens: auto;
  display: inline;
}

.search-dropdown-subtitle-wrapper {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.search-dropdown-subtitle {
  font-size: 13px;
  line-height: 1.5;
  color: var(--ds-text-muted);
  margin: 0;
}

.search-dropdown-image-container {
  flex-shrink: 0;
  width: 60px;
  height: 60px;
  min-width: 60px;
  border-radius: 8px;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}

.restaurant-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.image-placeholder {
  width: 100%;
  height: 100%;
  background-color: var(--ds-surface-subtle);
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Mobile slot cards */
.slot-card-mobile {
  border: 1px solid rgba(0, 0, 0, 0.12);
}

.slot-details-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

.slot-detail-item {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.slot-detail-label {
  font-size: 12px;
  color: rgba(0, 0, 0, 0.6);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.slot-detail-value {
  font-size: 14px;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.87);
}
</style>

<route lang="yaml">
meta:
  layout: website
  action: read
  subject: AclDemo
  allowLoggedInOnly: true
</route>
